import { useMemo, useState, MouseEvent } from "react";
import AppsIcon from "@mui/icons-material/Apps";
import { Box, Grid, IconButton, Popover, Typography } from "@mui/material";
import { useGetIdentity } from "react-admin";

/** Ficheiros em `public/AppIcons/` (caminho relativo ao `base` do Vite). */
const APP_ICONS = {
  crm: `${import.meta.env.BASE_URL}AppIcons/crm.png`,
  wms: `${import.meta.env.BASE_URL}AppIcons/wms.png`,
  /** Ícone ERP (ficheiro legado `wms.png` neste repositório). */
  erp: `${import.meta.env.BASE_URL}AppIcons/wms.png`,
} as const;

type AppLink = {
  label: string;
  href: string;
  iconSrc: (typeof APP_ICONS)[keyof typeof APP_ICONS];
};

const buildAppUrls = (homologacao: boolean | undefined) => {
  if (homologacao) {
    const base = "https://sandbox.rnge.com.br";
    return {
      crm: `${base}/crm/index.html#/`,
      wms: `${base}/wms/index.html#/`,
      erp: `${base}/app/index.html#/`,
    };
  }
  return {
    crm: "https://crm.rnge.com.br/#/",
    wms: "https://wms.rnge.com.br/#/",
    erp: "https://app.rnge.com.br/#/",
  };
};

const AppsMenu = () => {
  const { identity } = useGetIdentity();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const apps: AppLink[] = useMemo(() => {
    const u = buildAppUrls(identity?.homologacao);
    return [
      { label: "CRM", href: u.crm, iconSrc: APP_ICONS.crm },
      { label: "ERP", href: u.erp, iconSrc: APP_ICONS.erp },
      { label: "WMS", href: u.wms, iconSrc: APP_ICONS.wms },
    ];
  }, [identity?.homologacao]);

  const handleOpen = (e: MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleOpen}
        aria-label="Outros sistemas"
      >
        <AppsIcon />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{ sx: { p: 2, width: 240 } }}
      >
        <Grid container spacing={2}>
          {apps.map(({ label, href, iconSrc }) => (
            <Grid item xs={4} key={label}>
              <Box
                component="a"
                href={href}
                onClick={handleClose}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  textDecoration: "none",
                  color: "text.primary",
                  gap: 0.5,
                }}
              >
                <Box
                  component="img"
                  src={iconSrc}
                  alt={label}
                  sx={{ width: 40, height: 40, display: "block" }}
                />
                <Typography variant="caption">{label}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Popover>
    </>
  );
};

export default AppsMenu;
